import * as React from 'react';
import { useWindowEventListener } from '../helpers/hooks/use-window-event-listener';
import { useTheme } from '../theme-provider';
export function useThumbKeyHandlers(min, max, step, onSlidingChange, onValueChange, disabled = false) {
    const isRtl = useTheme().direction === 'rtl';
    const pressedKeys = React.useRef(new Set());
    const undoSlidingTimeout = React.useRef(undefined);
    const move = React.useCallback((increment) => (old) => {
        if (!Array.isArray(step)) {
            return old + increment * step;
        }
        const currentIndex = step.findIndex((step) => Math.abs(old - step) < 0.0001);
        const nextIndex = currentIndex + increment;
        if (nextIndex < 0) {
            return min;
        }
        if (nextIndex >= step.length - 1) {
            return max;
        }
        return step[nextIndex];
    }, [max, min, step]);
    const handleSlideEnd = React.useCallback(() => {
        onSlidingChange(false);
        window.clearTimeout(undoSlidingTimeout.current);
        pressedKeys.current.clear();
    }, [onSlidingChange]);
    const onKeyDown = React.useCallback((event) => {
        if (disabled) {
            return;
        }
        let change;
        switch (event.key) {
            case 'ArrowLeft':
                change = move(isRtl ? 1 : -1);
                break;
            case 'ArrowDown':
                change = move(-1);
                break;
            case 'ArrowRight':
                change = move(isRtl ? -1 : 1);
                break;
            case 'ArrowUp':
                change = move(1);
                break;
            case 'PageDown':
                change = move(-10);
                break;
            case 'PageUp':
                change = move(10);
                break;
            case 'Home':
                change = () => min;
                break;
            case 'End':
                change = () => max;
                break;
        }
        if (!change) {
            return;
        }
        pressedKeys.current.add(event.key);
        window.clearTimeout(undoSlidingTimeout.current);
        // safety net if no keyup event fired
        undoSlidingTimeout.current = window.setTimeout(handleSlideEnd, 600);
        onSlidingChange(true);
        onValueChange(change);
        event.preventDefault();
        event.stopPropagation();
    }, [
        disabled,
        handleSlideEnd,
        onSlidingChange,
        onValueChange,
        move,
        isRtl,
        min,
        max,
    ]);
    const onKeyUp = React.useCallback((event) => {
        if (pressedKeys.current.size > 0) {
            pressedKeys.current.delete(event.key);
            if (pressedKeys.current.size === 0) {
                handleSlideEnd();
            }
        }
    }, [handleSlideEnd]);
    return { onKeyDown, onKeyUp };
}
function getSliderValueByXCoordinate(xCoordinate, min, max, railWidth) {
    return (xCoordinate / railWidth) * (max - min) + min;
}
export function usePointerDownHandler(min, max, railElementRef, thumbElementRef, onSlidingChange, onValueChange, disabled = false) {
    const isRtl = useTheme().direction === 'rtl';
    /** only handle move and up listeners for same pointerId as down, so
     * multi-touch doesn't break */
    const pointerId = React.useRef();
    const pointerUpListener = useWindowEventListener('pointerup');
    const pointerCancelListener = useWindowEventListener('pointercancel');
    const pointerMoveListener = useWindowEventListener('pointermove');
    const pointerUpOrCancelHandler = React.useCallback((event) => {
        if (event.pointerId === pointerId.current) {
            onSlidingChange(false);
            pointerUpListener.unregister();
            pointerMoveListener.unregister();
            pointerCancelListener.unregister();
            pointerId.current = undefined;
        }
    }, [
        onSlidingChange,
        pointerUpListener,
        pointerMoveListener,
        pointerCancelListener,
    ]);
    const pointerMoveHandler = React.useCallback((event) => {
        if (event.pointerId === pointerId.current && railElementRef.current) {
            const { left, right } = railElementRef.current.getBoundingClientRect();
            const diffX = isRtl ? right - event.pageX : event.pageX - left;
            const railWidth = railElementRef.current.clientWidth;
            onValueChange(() => getSliderValueByXCoordinate(diffX, min, max, railWidth));
            event.preventDefault();
            event.stopPropagation();
        }
    }, [isRtl, max, min, onValueChange, railElementRef]);
    return React.useCallback((event) => {
        var _a;
        if (!disabled) {
            onSlidingChange(true);
            event.stopPropagation();
            (_a = thumbElementRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            pointerId.current = event.pointerId;
            // pointer move and up listeners should be on whole window, so state doesn't get stuck
            // when pointer moves away from element, while being pressed.
            pointerUpListener.register(pointerUpOrCancelHandler);
            pointerCancelListener.register(pointerUpOrCancelHandler);
            pointerMoveListener.register(pointerMoveHandler);
        }
    }, [
        disabled,
        onSlidingChange,
        thumbElementRef,
        pointerUpListener,
        pointerUpOrCancelHandler,
        pointerCancelListener,
        pointerMoveListener,
        pointerMoveHandler,
    ]);
}
